import {
  GetMapOfSpeakerLayoutRequest,
  GetMapOfSpeakerLayoutResponse,
  HearSpaceSpeaker
} from '../types/services/hearSpace';
import speakersData from '../toolConstants/conferenceroomDesigner/speakers.json';
import { showError } from '../utilities/toastManager';
import { createHttpClient } from './shared/httpClient';
import { ApiError } from './shared/apiError';

/**
 * HearSpace Acoustics Engine Service
 *
 * Service for interacting with the HearSpace Acoustics Engine API.
 * API Documentation: https://hearspaceakustxengine20250725.azurewebsites.net/api/GetUserGuide?format=html
 *
 * @module hearSpaceService
 */

const HEARSPACE_BASE_URL = process.env.REACT_APP_ACOUSTICS_API_URL;
const hearSpaceClient = createHttpClient({ baseUrl: HEARSPACE_BASE_URL || '' });

/**
 * Speaker model mapping from internal model names to HearSpace API names
 * Maps from "Model" field (e.g., "Desono C-IC6") to "Name" field (e.g., "C-IC6")
 */
const SPEAKER_MODEL_MAPPING: Record<string, string> = speakersData.reduce((acc, speaker) => {
  acc[speaker.Model] = speaker.Name;
  return acc;
}, {} as Record<string, string>);

/**
 * Get the HearSpace API speaker name from the internal model name
 *
 * @param modelName - Internal model name (e.g., "Desono C-IC6")
 * @returns HearSpace API name (e.g., "C-IC6"), or the original name if not found
 */
export const getHearSpaceModelName = (modelName: string): string => {
  // Check if model exists in mapping
  if (SPEAKER_MODEL_MAPPING[modelName]) {
    return SPEAKER_MODEL_MAPPING[modelName];
  }

  // Fallback: C-IC6LP and C-IC6LP-TAA use same acoustic data as C-IC6
  // This prevents errors when these models aren't supported by the API
  // All three models use the same driver and have identical acoustic properties
  if (modelName === 'Desono C-IC6LP' || modelName === 'Desono C-IC6LP-TAA') {
    return 'C-IC6';
  }

  // Fallback: CBC 2500 bars use same acoustic data as ABC 2500a/VBC 2500a
  // CBC 2500 CM (cable mount with camera) and CBC 2500 PM (pole mount) are the same device
  // with identical acoustic properties to ABC 2500a, just different mounting options
  // The HearSpace API uses "Parle Bar" (no accent) for all bar devices
  if (modelName === 'Parlé CBC 2500 CM' || modelName === 'Parlé CBC 2500 PM') {
    return 'Parle Bar';
  }

  return modelName;
};

/**
 * Validate and adjust receiver spacing to ensure grid size is within API limits
 * API Constraint: Receiver grid must contain 100-5000 receivers
 *
 * @param dimensionX - Room length in meters
 * @param dimensionY - Room width in meters
 * @param requestedSpacing - Desired receiver spacing in meters
 * @returns Adjusted spacing that ensures valid grid size, or null if impossible
 */
export const validateReceiverSpacing = (
  dimensionX: number,
  dimensionY: number,
  requestedSpacing: number
): { spacing: number; receiverCount: number; adjusted: boolean } | null => {
  // Calculate receiver count with requested spacing
  const countX = Math.ceil(dimensionX / requestedSpacing);
  const countY = Math.ceil(dimensionY / requestedSpacing);
  const receiverCount = countX * countY;

  // Check if within limits (100-5000 receivers)
  if (receiverCount >= 100 && receiverCount <= 5000) {
    return { spacing: requestedSpacing, receiverCount, adjusted: false };
  }

  // Too few receivers - reduce spacing
  if (receiverCount < 100) {
    // Calculate spacing for exactly 100 receivers
    const minSpacingForCount = Math.sqrt((dimensionX * dimensionY) / 100);
    const adjustedSpacing = Math.min(requestedSpacing * 0.9, minSpacingForCount);
    const newCountX = Math.ceil(dimensionX / adjustedSpacing);
    const newCountY = Math.ceil(dimensionY / adjustedSpacing);
    const newCount = newCountX * newCountY;

    if (newCount >= 100 && newCount <= 5000) {
      return { spacing: Number(adjustedSpacing.toFixed(3)), receiverCount: newCount, adjusted: true };
    }
  }

  // Too many receivers - increase spacing
  if (receiverCount > 5000) {
    // Iteratively find spacing that results in ≤ 5000 receivers
    // Account for Math.ceil rounding up by targeting slightly under 5000
    let adjustedSpacing = Math.sqrt((dimensionX * dimensionY) / 4900); // Target 4900 to leave room for ceiling
    let newCountX = Math.ceil(dimensionX / adjustedSpacing);
    let newCountY = Math.ceil(dimensionY / adjustedSpacing);
    let newCount = newCountX * newCountY;

    // If still over, incrementally increase spacing until we're under 5000
    let attempts = 0;
    while (newCount > 5000 && attempts < 20) {
      adjustedSpacing *= 1.02; // Increase by 2% each iteration
      newCountX = Math.ceil(dimensionX / adjustedSpacing);
      newCountY = Math.ceil(dimensionY / adjustedSpacing);
      newCount = newCountX * newCountY;
      attempts++;
    }

    if (newCount >= 100 && newCount <= 5000) {
      return { spacing: Number(adjustedSpacing.toFixed(3)), receiverCount: newCount, adjusted: true };
    }
  }

  // Unable to find valid spacing
  return null;
};

/**
 * Helper function to convert Redux speaker layout to HearSpace speaker format
 *
 * COORDINATE SYSTEM DOCUMENTATION:
 * ================================
 * Room Orientation (Floorplan View):
 *   - LEFT wall (X=0) = FRONT of room (where presentation screen is)
 *   - RIGHT wall (X=max) = BACK of room
 *   - TOP wall (Y=0) = Side wall
 *   - BOTTOM wall (Y=max) = Opposite side wall
 *   - Origin (0,0) = TOP-LEFT corner of room
 *
 * Angle Conventions:
 *   - Our internal: Standard atan2 (0° = +X axis = toward BACK of room, counter-clockwise)
 *   - HearSpace API: 0° = -Y axis, 90° = +X axis, 180° = +Y, 270° = -X
 *   - Conversion: HearSpace angle = (our angle + 90°) mod 360°
 *
 * Wall Speaker Mounting:
 *   - rotation = 0° : Mounted on LEFT wall (front), facing RIGHT (toward audience)
 *   - rotation = 90° : Mounted on TOP wall, facing DOWN
 *   - rotation = 180° : Mounted on RIGHT wall (back), facing LEFT
 *   - rotation = 270° : Mounted on BOTTOM wall, facing UP
 *
 * @param speakers - Array of speakers from Redux state
 * @returns Array of HearSpace speaker configurations
 */
export const convertToHearSpaceSpeakers = (
  speakers: Array<{
    model?: string;
    modelName?: string;
    location?: { x: number; y: number; z?: number };
    height?: number;
    delay?: number;
    gain?: number;
    horAngle?: number;
    verAngle?: number;
    rotation?: number;
  }>,
  defaultHeight = 2.8956
): HearSpaceSpeaker[] => {
  return speakers.map((speaker, index) => {
    // Get the internal model name from Redux state
    const internalModelName = speaker.modelName || speaker.model || '';

    // Convert to HearSpace API name using the mapping
    const hearSpaceModelName = getHearSpaceModelName(internalModelName);

    // Check if this is a bar speaker
    const isBarSpeaker = speaker.model === 'barspkr' ||
                         speaker.modelName?.includes('CBC 2500') ||
                         speaker.modelName?.includes('ABC 2500') ||
                         speaker.modelName?.includes('VBC 2500');

    // VBC/ABC bars are wall-mounted, CBC bars are ceiling-mounted
    const isWallMountedBar = (speaker.modelName?.includes('VBC') && !speaker.modelName?.includes('CBC')) ||
                             speaker.modelName?.includes('ABC 2500');

    // For CBC ceiling bars only, ignore existing verAngle to recalculate straight down
    if (isBarSpeaker && !isWallMountedBar && speaker.verAngle !== undefined) {
      speaker = { ...speaker, verAngle: undefined };
    }

    // Convert angle conventions (see coordinate system documentation above)
    // Both ceiling and wall speakers use room-absolute angles (0°=right, 90°=down, 180°=left, 270°=up)
    // Just apply the standard +90° conversion to HearSpace coordinates for both types
    // VBC/ABC bars are wall-mounted even though they might not have rotation set in all cases
    const isWallSpeaker = speaker.rotation !== undefined || isWallMountedBar;
    let hearSpaceHorAngle = speaker.horAngle;

    if (hearSpaceHorAngle !== undefined && Number.isFinite(hearSpaceHorAngle)) {
      if (isWallSpeaker && speaker.rotation !== undefined) {
        // For wall speakers: Account for physical rotation
        // The rotation indicates which wall the speaker is on (0°, 90°, 180°, 270°)
        // Subtract rotation to convert from room coordinates to speaker-relative coordinates
        const relativeAngle = (hearSpaceHorAngle - speaker.rotation + 360) % 360;
        hearSpaceHorAngle = (relativeAngle + 90) % 360;
      } else {
        // Convert ceiling speaker angle (and wall bars without rotation): room coordinates → HearSpace coordinates
        hearSpaceHorAngle = (hearSpaceHorAngle + 90) % 360;
      }
    } else {
      // Default for speakers without horAngle stored
      // Ceiling/pendant speakers and bars default to facing forward
      hearSpaceHorAngle = 180; // Standard ceiling orientation in HearSpace coordinates
    }

    // Prefer location.z if available (calculated fresh in getDeviceLayouts), otherwise fall back to speaker.height
    const speakerZ = speaker.location?.z || speaker.height || defaultHeight;

    // Compute verAngle: use stored value if valid, otherwise derive from height
    // Uses straight-down formula for ceiling speakers, preserves/calculates wall-mounted angles for VBC/ABC.
    // ReceiverHeight matches ReceiverHeight in the API request (1.2 m = standard seated listening height).
    let hearSpaceVerAngle: number | undefined;
    const receiverHeight = 1.2;
    const heightDiff = speakerZ - receiverHeight;

    if (Number.isFinite(speaker.verAngle)) {
      hearSpaceVerAngle = speaker.verAngle;
    } else if (isWallSpeaker) {
      // Wall speakers without verAngle: calculate based on height differential
      // Assume aiming toward center of room (horizontal distance ~2-3m typical)
      const horizontalDistance = 2.5; // Typical room depth from wall
      hearSpaceVerAngle = Math.round(-(Math.atan2(heightDiff, horizontalDistance) * 180) / Math.PI);
    } else {
      // Ceiling speakers: aim straight down (distance = 0.001 = nearly vertical)
      hearSpaceVerAngle = Math.round(-(Math.atan2(heightDiff, 0.001) * 180) / Math.PI * 10) / 10;
    }

    const hearSpaceSpeaker = {
      ModelName: hearSpaceModelName,
      Location: {
        x: speaker.location?.x || 0,
        y: speaker.location?.y || 0,
        z: speakerZ,
      },
      Delay: speaker.delay,
      TotalGain: speaker.gain,
      HorAngle: hearSpaceHorAngle,
      ...(hearSpaceVerAngle !== undefined && { VerAngle: hearSpaceVerAngle }),
    };

    return hearSpaceSpeaker;
  });
};

/**
 * Call the GetMapOfSpeakerLayout endpoint to generate acoustic mappings
 * including SPL and STI calculations across a room's receiver grid.
 *
 * @param request - The request payload containing room dimensions, speakers, and configuration
 * @returns Promise resolving to the response with SPL matrices and performance data
 * @throws Error if the API request fails
 *
 * @example
 * ```typescript
 * const request: GetMapOfSpeakerLayoutRequest = {
 *   DimensionX: 6.096, // meters
 *   DimensionY: 4.2672, // meters
 *   DimensionZ: 2.8956, // meters
 *   ReceiverSpacing: 0.5,
 *   Speakers: [
 *     {
 *       ModelName: 'Desono C-IC6',
 *       Location: { x: 3.0, y: 2.0, z: 2.8 },
 *     }
 *   ],
 *   DirectSoundOnly: true,
 *   IncludeDirectSplAwtd: true,
 * };
 *
 * const response = await getMapOfSpeakerLayout(request);
 * console.log('Average SPL:', response.Performance?.AverageSpl);
 * ```
 */
export const getMapOfSpeakerLayout = async (
  request: GetMapOfSpeakerLayoutRequest
): Promise<GetMapOfSpeakerLayoutResponse> => {
  if (!HEARSPACE_BASE_URL) {
    const errorMsg = 'Acoustics API URL is not configured. Please contact support.';
    showError(errorMsg);
    throw new Error(errorMsg);
  }

  let raw: string | GetMapOfSpeakerLayoutResponse;
  try {
    raw = await hearSpaceClient.post<string | GetMapOfSpeakerLayoutResponse>(
      '/GetMapOfSpeakerLayout',
      { body: request }
    );
  } catch (e) {
    if (e instanceof ApiError) {
      let errorMessage: string | null = null;
      if (typeof e.body === 'string') {
        try {
          const errorData = JSON.parse(e.body) as Record<string, unknown>;
          errorMessage = (errorData.ErrorMessage as string) || (errorData.Error as string) || null;
        } catch { /* ignore */ }
      } else if (e.body !== null && typeof e.body === 'object') {
        const bodyObj = e.body as Record<string, unknown>;
        errorMessage = (bodyObj.ErrorMessage as string) || (bodyObj.Error as string) || null;
      }
      const finalErrorMessage = errorMessage || `HearSpace API error: ${e.status} ${e.statusText}`;
      showError(finalErrorMessage);
      throw new Error(finalErrorMessage);
    }
    throw e;
  }

  // The API double-encodes its response: httpClient calls response.json() (first decode), which
  // yields a JSON string instead of an object. JSON.parse(raw) below does the second decode.
  const data: GetMapOfSpeakerLayoutResponse = typeof raw === 'string' ? JSON.parse(raw) : raw;

  if (data.Error) {
    throw new Error(`HearSpace API error: ${data.Error}`);
  }

  return data;
};

/**
 * Build a GetMapOfSpeakerLayout request from Redux state
 *
 * This is a helper function to construct the request payload from typical Redux state structure.
 * You can customize this based on your actual Redux state shape.
 *
 * @param roomData - Room data from Redux state
 * @returns Request payload ready for the API
 *
 * @example
 * ```typescript
 * const roomData = {
 *   dimensions: { length: 6.096, width: 4.2672 },
 *   ceilingHeight: { min: 2.8956, max: 2.8956 },
 *   speakers: {
 *     layout: [
 *       { modelName: 'Desono C-IC6', location: { x: 3.0, y: 2.0 } }
 *     ],
 *     height: 2.8956,
 *   },
 * };
 *
 * const request = buildRequestFromReduxState(roomData);
 * const response = await getMapOfSpeakerLayout(request);
 * ```
 */
export const buildRequestFromReduxState = (
  roomData: {
    dimensions: { length: number; width: number };
    ceilingHeight?: { min: number; max: number };
    speakers?: {
      layout?: Array<{
        model?: string;
        modelName?: string;
        location?: { x: number; y: number };
        height?: number;
      }>;
      height?: number;
    };
    materials?: { walls?: string; ceiling?: string; floor?: string };
    acoustics?: string;
  },
  options?: {
    receiverSpacing?: number;
    includeSpl?: boolean;
    directSoundOnly?: boolean;
    noiseLevel?: number;
  }
): GetMapOfSpeakerLayoutRequest => {
  const defaultHeight = roomData.ceilingHeight?.min || 2.8956;
  const speakerHeight = roomData.speakers?.height || defaultHeight;

  // Convert speaker layout to HearSpace format
  const speakers = roomData.speakers?.layout || [];
  const hearSpaceSpeakers = convertToHearSpaceSpeakers(speakers, speakerHeight);

  // Validate and adjust receiver spacing if needed
  const requestedSpacing = options?.receiverSpacing || 0.1;
  const spacingValidation = validateReceiverSpacing(
    roomData.dimensions.length,
    roomData.dimensions.width,
    requestedSpacing
  );

  if (!spacingValidation) {
    throw new Error(
      `Invalid room dimensions for HearSpace API: ${roomData.dimensions.length}m × ${roomData.dimensions.width}m. ` +
      `Cannot create valid receiver grid (100-5000 receivers required).`
    );
  }

  const finalSpacing = spacingValidation.spacing;

  // Build the request
  const request: GetMapOfSpeakerLayoutRequest = {
    DimensionX: roomData.dimensions.length,
    DimensionY: roomData.dimensions.width,
    DimensionZ: defaultHeight,
    ReceiverSpacing: finalSpacing,
    ReceiverHeight: 1.2, // Standard listening height (seated)
    Speakers: hearSpaceSpeakers,
    DirectSoundOnly: options?.directSoundOnly ?? true, // Default to direct sound only
    IncludeDirectSplAwtd: options?.includeSpl ?? true,
    IncludeDirectSplVoiceBand: false,
    IncludeDirectSplOctBands: false,
  };

  // Add room acoustics if not direct sound only
  if (!request.DirectSoundOnly) {
    // TODO: Map material names from Redux to HearSpace material names
    // For now, using null/blank as per user request
    request.Materials = [];
    request.ConsiderFurnitureAndOccupants = false;
    request.NoiseLevel = options?.noiseLevel || 35; // Default office noise level
  }

  return request;
};

export default {
  getMapOfSpeakerLayout,
  buildRequestFromReduxState,
  convertToHearSpaceSpeakers,
  getHearSpaceModelName,
  validateReceiverSpacing,
};
